import {
  BET_PHASE_MS,
  POST_CRASH_DELAY_MS,
  GROWTH_RATE,
  MIN_CRASH_POINT,
  CRASH_HOUSE_EDGE,
  MIN_CASHOUT_MULTIPLIER,
  MAX_HISTORY,
  MILESTONES,
} from './constants';

export enum RoundState {
  BET_TIME = 'BET_TIME',
  ROUND_START = 'ROUND_START',
  RUNNING = 'RUNNING',
  CRASH = 'CRASH'
}

export interface Bet {
  id: string;
  slotId: number;
  amount: number;
  username: string;
  avatar?: string;
  timestamp: number;
  cashoutMultiplier?: number;
  targetCashoutMultiplier?: number;
  isActive: boolean;
}

export interface Round {
  id: string;
  state: RoundState;
  startTime: number;
  crashPoint: number;
  multiplier: number;
  bets: Bet[];
  serverSeed: string;
  clientSeed: string;
  gameSeed: string;
}

export interface GameEventMap {
  multiplierUpdate: { multiplier: number };
  stateChange: { state: RoundState };
  roundStart: { round: Round };
  rocketLaunch: undefined;
  betPlaced: { bet: Bet };
  betActivated: { bet: Bet };
  betCanceled: { bet: Bet };
  cashout: { bet: Bet; winAmount: number; multiplier: number; auto?: boolean };
  betWin: { bet: Bet; winAmount: number };
  betLoss: { bet: Bet };
  crash: { multiplier: number; round: Round };
  historyUpdate: { history: Round[] };
  milestone: { multiplier: number };
  paused: undefined;
  resumed: undefined;
  autoBetToggle: { slotId: number; enabled: boolean };
}

export interface GameEvent<K extends keyof GameEventMap = keyof GameEventMap> {
  type: K;
  data: GameEventMap[K];
}

type EventCallback<K extends keyof GameEventMap = keyof GameEventMap> = (event: GameEvent<K>) => void;

const SEED_LENGTH = 16;
const ID_RANDOM_LENGTH = 9;
const CRASH_GUARD_EPSILON = 1.0000001;
const MS_PER_SEC = 1000;

export class Game {
  private state: RoundState = RoundState.BET_TIME;
  private currentRound: Round | null = null;
  private multiplier = 1.0;
  private rafId: number | null = null;
  private startTime = 0;
  private crashPoint = 1.0;
  private timeToCrashSec = 0;
  private listeners = new Map<string, Set<EventCallback<keyof GameEventMap>>>();
  private bets: Bet[] = [];
  private autoBets = new Map<number, boolean>();
  private roundHistory: Round[] = [];
  private firedMilestones = new Set<number>();
  private paused = false;
  private roundCounter = 0;

  constructor() {
    this.initializeRound();
  }

  on<K extends keyof GameEventMap>(eventType: K, callback: EventCallback<K>): void {
    if (!this.listeners.has(eventType as string)) {
      this.listeners.set(eventType as string, new Set());
    }
    this.listeners.get(eventType as string)!.add(callback as EventCallback<keyof GameEventMap>);
  }

  off<K extends keyof GameEventMap>(eventType: K, callback: EventCallback<K>): void {
    this.listeners.get(eventType as string)?.delete(callback as EventCallback<keyof GameEventMap>);
  }

  private emit<K extends keyof GameEventMap>(
    ...args: GameEventMap[K] extends undefined ? [eventType: K] : [eventType: K, data: GameEventMap[K]]
  ): void {
    const [eventType, data] = args;
    const event = { type: eventType, data } as GameEvent<K>;
    const callbacks = this.listeners.get(eventType as string);
    if (!callbacks) return;
    for (const cb of callbacks) {
      try {
        (cb as EventCallback<K>)(event);
      } catch { }
    }
  }

  private initializeRound(): void {
    this.state = RoundState.BET_TIME;
    this.multiplier = 1.0;
    this.startTime = Date.now();
    this.crashPoint = this.generateCrashPoint();
    this.roundCounter++;

    this.currentRound = {
      id: this.generateId('round'),
      state: this.state,
      startTime: this.startTime,
      crashPoint: this.crashPoint,
      multiplier: this.multiplier,
      bets: [],
      serverSeed: this.generateSeed(),
      clientSeed: this.generateSeed(),
      gameSeed: ''
    };

    this.emit('roundStart', { round: this.currentRound });
    this.emit('stateChange', { state: this.state });
    this.emit('multiplierUpdate', { multiplier: this.multiplier });
    this.firedMilestones.clear();

    setTimeout(() => this.startRound(), BET_PHASE_MS);
  }

  private startRound(): void {
    if (this.state !== RoundState.BET_TIME) return;

    this.state = RoundState.ROUND_START;
    this.emit('stateChange', { state: this.state });

    this.state = RoundState.RUNNING;
    this.startTime = Date.now();
    this.timeToCrashSec = Math.max(0, Math.log(Math.max(CRASH_GUARD_EPSILON, this.crashPoint)) / GROWTH_RATE);
    this.emit('stateChange', { state: this.state });
    this.emit('rocketLaunch');

    for (const bet of this.bets) {
      if (bet.isActive) {
        this.emit('betActivated', { bet });
      }
    }

    this.startTicking();
  }

  private startTicking(): void {
    if (this.rafId !== null) return;
    this.paused = false;

    const tick = () => {
      if (this.state !== RoundState.RUNNING) {
        this.rafId = null;
        return;
      }
      this.updateMultiplier();
      if (this.state === RoundState.RUNNING) {
        this.rafId = requestAnimationFrame(tick);
      }
    };

    this.rafId = requestAnimationFrame(tick);
  }

  private stopTicking(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private updateMultiplier(): void {
    const elapsedSec = (Date.now() - this.startTime) / MS_PER_SEC;
    const clampedT = Math.min(elapsedSec, this.timeToCrashSec);
    this.multiplier = Math.exp(GROWTH_RATE * clampedT);

    if (clampedT >= this.timeToCrashSec) {
      this.multiplier = Math.min(this.multiplier, this.crashPoint);
    }

    this.checkMilestones();
    this.emit('multiplierUpdate', { multiplier: this.multiplier });
    this.processAutoCashouts();

    if (elapsedSec >= this.timeToCrashSec || this.multiplier >= this.crashPoint) {
      this.multiplier = this.crashPoint;
      this.crashRound();
    }
  }

  private checkMilestones(): void {
    for (const milestone of MILESTONES) {
      if (this.multiplier >= milestone && !this.firedMilestones.has(milestone)) {
        this.firedMilestones.add(milestone);
        this.emit('milestone', { multiplier: milestone });
      }
    }
  }

  private processAutoCashouts(): void {
    for (const bet of this.bets) {
      if (
        bet.isActive &&
        bet.targetCashoutMultiplier &&
        !bet.cashoutMultiplier &&
        this.multiplier >= bet.targetCashoutMultiplier
      ) {
        bet.cashoutMultiplier = bet.targetCashoutMultiplier;
        bet.isActive = false;
        const winAmount = bet.amount * bet.cashoutMultiplier;
        this.emit('cashout', {
          bet,
          winAmount,
          multiplier: bet.cashoutMultiplier,
          auto: true,
        });
      }
    }
    this.bets = this.bets.filter(b => b.isActive);
  }

  pause(): void {
    if (this.state !== RoundState.RUNNING || this.paused) return;
    this.paused = true;
    this.stopTicking();
    this.emit('paused');
  }

  resume(): void {
    if (this.state !== RoundState.RUNNING || !this.paused) return;
    this.startTicking();
    this.emit('resumed');
  }

  private crashRound(): void {
    this.state = RoundState.CRASH;
    this.stopTicking();

    if (this.currentRound) {
      this.currentRound.gameSeed = this.generateSeed();
      this.currentRound.state = this.state;
      this.currentRound.multiplier = this.crashPoint;
    }

    this.emit('stateChange', { state: this.state });
    this.emit('crash', {
      multiplier: this.crashPoint,
      round: this.currentRound!,
    });

    this.processRoundResults();

    if (this.currentRound) {
      this.roundHistory.unshift(this.currentRound);
      if (this.roundHistory.length > MAX_HISTORY) {
        this.roundHistory.pop();
      }
      this.emit('historyUpdate', { history: this.roundHistory });
    }

    setTimeout(() => this.initializeRound(), POST_CRASH_DELAY_MS);
  }

  private processRoundResults(): void {
    for (const bet of this.bets) {
      if (!bet.isActive) continue;
      bet.isActive = false;
      if (bet.cashoutMultiplier && bet.cashoutMultiplier <= this.multiplier) {
        this.emit('betWin', {
          bet,
          winAmount: bet.amount * bet.cashoutMultiplier,
        });
      } else {
        this.emit('betLoss', { bet });
      }
    }
    this.bets = this.bets.filter(b => !b.isActive);
  }

  placeBet(
    amount: number,
    slotId: number,
    username = 'Player',
    targetCashoutMultiplier?: number,
    avatar?: string,
  ): Bet {
    if (this.state !== RoundState.BET_TIME) {
      throw new Error('Betting is not allowed at this time');
    }
    if (amount <= 0) {
      throw new Error('Bet amount must be positive');
    }
    const bet: Bet = {
      id: this.generateId('bet'),
      slotId,
      amount,
      username,
      avatar,
      timestamp: Date.now(),
      isActive: true,
      targetCashoutMultiplier:
        targetCashoutMultiplier && targetCashoutMultiplier >= MIN_CASHOUT_MULTIPLIER
          ? targetCashoutMultiplier
          : undefined,
    };
    this.bets.push(bet);
    if (this.currentRound) {
      this.currentRound.bets.push(bet);
    }
    this.emit('betPlaced', { bet });
    return bet;
  }

  cancelBet(betId: string): boolean {
    if (this.state !== RoundState.BET_TIME) return false;
    const idx = this.bets.findIndex(b => b.id === betId && b.isActive);
    if (idx === -1) return false;
    const bet = this.bets[idx];
    bet.isActive = false;
    this.bets.splice(idx, 1);
    if (this.currentRound) {
      const roundIdx = this.currentRound.bets.findIndex(b => b.id === betId);
      if (roundIdx !== -1) this.currentRound.bets.splice(roundIdx, 1);
    }
    this.emit('betCanceled', { bet });
    return true;
  }

  cashOut(betId: string): boolean {
    const bet = this.bets.find(b => b.id === betId && b.isActive);
    if (!bet || this.state !== RoundState.RUNNING) return false;
    bet.cashoutMultiplier = this.multiplier;
    bet.isActive = false;
    const winAmount = bet.amount * bet.cashoutMultiplier;
    this.emit('cashout', {
      bet,
      winAmount,
      multiplier: this.multiplier,
    });
    return true;
  }

  setAutoBet(slotId: number, enabled: boolean): void {
    this.autoBets.set(slotId, enabled);
    this.emit('autoBetToggle', { slotId, enabled });
  }

  getCurrentState(): RoundState { return this.state; }
  getCurrentMultiplier(): number { return this.multiplier; }
  getCurrentRound(): Round | null { return this.currentRound; }
  getBets(): Bet[] { return this.bets; }
  getRoundHistory(): Round[] { return this.roundHistory; }
  getRoundNumber(): number { return this.roundCounter; }
  isAutoBetEnabled(slotId: number): boolean { return this.autoBets.get(slotId) || false; }

  private generateCrashPoint(): number {
    const random = Math.random();
    return Math.max(MIN_CRASH_POINT, CRASH_HOUSE_EDGE / (1 - random));
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 2 + ID_RANDOM_LENGTH)}`;
  }

  private generateSeed(): string {
    return Math.random().toString(36).substring(2, 2 + SEED_LENGTH);
  }

  destroy(): void {
    this.stopTicking();
    this.listeners.clear();
  }
}
